import { useEffect, useMemo, useRef, useState } from "react";

import { Label } from "./Field";

type SliderBaseProps<T> = {
  label?: string;
  thumbLabels?: string[];
  selectedValues: T;
  onChange?: (value: T) => void;
  onChangeEnd?: (value: T) => void;
  minValue?: number;
  maxValue?: number;
  step?: number;
  className?: string;
  formatValue?: (value: number) => string;
  trackHeightClass?: string;
};

export interface ISliderProps<T> extends SliderBaseProps<T> {}

const defaultFormatter = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(value);

export function RangeSlider({
  label,
  thumbLabels,
  selectedValues,
  onChange,
  onChangeEnd,
  minValue = 0,
  maxValue = 100,
  step = 1,
  formatValue = defaultFormatter,
  trackHeightClass = "h-2",
  className,
}: ISliderProps<number[]>) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [values, setValues] = useState<number[]>([...selectedValues]);
  const valuesRef = useRef<number[]>(values);
  const [activeThumb, setActiveThumb] = useState<number | null>(null);

  useEffect(() => {
    valuesRef.current = [...selectedValues];
    setValues([...selectedValues]);
  }, [selectedValues]);

  const clamp = (val: number) => Math.min(maxValue, Math.max(minValue, val));

  const snap = (val: number) => Math.round(val / step) * step;

  const percentFromValue = (val: number) =>
    ((val - minValue) / (maxValue - minValue)) * 100;

  const setValueAt = (index: number, clientX: number, emitEnd?: boolean) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    const raw = minValue + ratio * (maxValue - minValue);
    const nextVal = clamp(snap(raw));

    const next = [...valuesRef.current];
    next[index] = nextVal;
    next.sort((a, b) => a - b);
    valuesRef.current = next;
    setValues(next);
    onChange?.(next);
    if (emitEnd) onChangeEnd?.(next);
  };

  const startDrag = (
    index: number,
    event: React.MouseEvent | React.TouchEvent,
  ) => {
    event.preventDefault();
    setActiveThumb(index);
    const move = (e: MouseEvent | TouchEvent) => {
      const clientX =
        "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      setValueAt(index, clientX, false);
    };
    const up = (e: MouseEvent | TouchEvent) => {
      const clientX =
        "changedTouches" in e
          ? e.changedTouches[0].clientX
          : (e as MouseEvent).clientX;
      setValueAt(index, clientX, true);
      setActiveThumb(null);
      window.removeEventListener("mousemove", move as any);
      window.removeEventListener("mouseup", up as any);
      window.removeEventListener("touchmove", move as any);
      window.removeEventListener("touchend", up as any);
    };
    window.addEventListener("mousemove", move as any);
    window.addEventListener("mouseup", up as any);
    window.addEventListener("touchmove", move as any, { passive: false });
    window.addEventListener("touchend", up as any);
  };

  const safeFormat = useMemo(() => formatValue, [formatValue]);

  const sorted = [...values].sort((a, b) => a - b);
  const leftPercent = values.length > 1 ? percentFromValue(sorted[0]) : 0;
  const rightPercent = percentFromValue(sorted[sorted.length - 1]);

  const trackHeightPx = 4;

  const mergedClassName = useMemo(
    () => ["flex flex-col gap-2 w-full", className].filter(Boolean).join(" "),
    [className],
  );

  return (
    <div className={mergedClassName}>
      {label && <Label>{label}</Label>}

      <div className="relative w-full h-6" ref={trackRef}>
        <div
          className={`absolute left-0 top-1/2 -translate-y-1/2 w-full ${trackHeightClass} rounded-pill bg-n200 dark:bg-n800`}
          style={{
            height: `${trackHeightPx}px`,
            borderRadius: `${trackHeightPx / 2}px`,
          }}
        />
        <div
          className={`absolute top-1/2 -translate-y-1/2 ${trackHeightClass} rounded-pill pointer-events-none bg-backgroundBrand`}
          style={{
            height: `${trackHeightPx}px`,
            borderRadius: `${trackHeightPx / 2}px`,
            left: `${leftPercent}%`,
            width: `${Math.max(0, rightPercent - leftPercent)}%`,
          }}
        />

        {values.map((value, index) => {
          const percent = percentFromValue(value);
          return (
            <button
              key={index}
              type="button"
              aria-label={thumbLabels?.[index] ?? `Value ${index + 1}`}
              className={`absolute top-1/2 -translate-y-1/2 translate-x-[-50%] w-4 h-4 rounded-pill border-2 border-borderBrand shadow-sm cursor-grab outline-none transition-colors duration-base ease-out hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-borderFocus ${
                activeThumb === index
                  ? "bg-n100 dark:bg-n700"
                  : "bg-backgroundTertiary"
              }`}
              style={{ left: `${Math.min(100, Math.max(0, percent))}%` }}
              onMouseDown={(e) => startDrag(index, e)}
              onTouchStart={(e) => startDrag(index, e)}>
              <span className="sr-only">{safeFormat(value)}</span>
              {activeThumb === index && (
                <span className="absolute -top-6 left-1/2 -translate-x-1/2 rounded-md bg-backgroundBrand px-2 py-1 text-pm font-medium text-contentOnBrand shadow-sm">
                  {safeFormat(value)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
